import { useState } from 'react';
import { cn } from '@/components/ui/utils';
import { ImageWithFallback } from '../image-with-fallback';
import type { ProgressiveImageProps } from '@/types';

/**
 * Внутренний компонент — state сбрасывается через key={src} в обёртке.
 * Нет useEffect для синхронизации — React пересоздаёт при смене src.
 */
function ProgressiveImageInner({
  src,
  alt,
  placeholderSrc,
  containerClassName,
  placeholderBg = 'bg-muted',
  className,
  style,
  ...rest
}: ProgressiveImageProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className={cn('relative overflow-hidden', containerClassName)}>
      {/* Blur placeholder - показываем пока грузится */}
      {isLoading && !hasError && (
        <div
          className={cn(
            'absolute inset-0 animate-pulse',
            placeholderBg
          )}
          style={{
            backdropFilter: 'blur(20px)',
          }}
        >
          {placeholderSrc && (
            <img
              src={placeholderSrc}
              alt=""
              className="w-full h-full object-cover opacity-50 blur-xl scale-110"
              aria-hidden="true"
            />
          )}
        </div>
      )}

      {/* Основное изображение */}
      <ImageWithFallback
        src={src}
        alt={alt}
        className={cn(
          'transition-opacity duration-500',
          isLoading && !hasError ? 'opacity-0' : 'opacity-100',
          className
        )}
        style={style}
        onLoad={handleLoad}
        onError={handleError}
        {...rest}
      />
    </div>
  );
}

/**
 * Компонент прогрессивной загрузки изображений
 * 
 * @description
 * Отображает низкокачественное превью во время загрузки основного изображения,
 * затем плавно переключается на полное изображение.
 *
 * Сброс состояния при смене src реализован через key-паттерн —
 * React пересоздаёт компонент, state инициализируется заново.
 */
export function ProgressiveImage(props: ProgressiveImageProps): JSX.Element {
  return <ProgressiveImageInner key={props.src} {...props} />;
}
